import useMapStore from "@/store/mapStore";
import { useState, useEffect } from "react";

interface ParkAssistResult {
  data: ParkAssistResultData[];
}

interface ParkAssistResultData {
  id: string;
  eventTime: string;
  plate: string;
  state: string;
  vehicleColor: string;
  vehicleBrand: string;
  vehicleType: string;
  imageUrl: string;
  fullImageUrl: string;
}

export interface ParkAssistImage {
  aisle: string;
  bayNo: number;
  id: string;
  site: string;
  plate: string;
  state: string;
  vehicleColor: string;
  vehicleBrand: string;
  vehicleType: string;
  imageUrl: string;
  fullImageUrl: string;
  buildingId: string;
  building: string;
  levelId: string;
  level: string;
  mapId: string;
}

export enum FetchStatus {
  Loading = "loading",
  Loaded = "loaded",
  Error = "error",
  TooManyRequests = "tooManyRequests",
}

export default function useParkAssist({ plateText }: { plateText: string }) {
  const [imageData, setImageData] = useState<ParkAssistImage[]>([]);
  const [fetchStatus, setFetchStatus] = useState<FetchStatus>(
    FetchStatus.Loading,
  );

  const appSession = useMapStore((state) => state.appSession);
  const site = "t5409q7v3BrP";

  useEffect(() => {
    const fetchImageData = async () => {
      try {
        // Set loading state
        setFetchStatus(FetchStatus.Loading);

        // Fetch results from the first API endpoint
        const resultsResponse = await fetch(
          `https://api.proximi.fi/limitless/car-finder/search?site=${site}&plate=${plateText}&offset=4`,
          {
            headers: {
              Authorization: `Bearer ${import.meta.env.VITE_PROXIMIIO_TOKEN}`,
            },
          },
        );

        if (resultsResponse.status === 429) {
          setFetchStatus(FetchStatus.TooManyRequests);
          return;
        }

        if (!resultsResponse.ok) {
          throw new Error(
            `Request failed with status ${resultsResponse.status}`,
          );
        }

        const results: ParkAssistResult = await resultsResponse.json();
        console.log("results", results.data);

        // Fetch images for each visit ID
        //@ts-ignore
        const imagePromises = results.data.map(async (result) => {
          const imageResponse = await fetch(
            `https://api.proximi.fi/limitless/car-finder/get?id=${result.id}`,
            {
              headers: {
                Authorization: `Bearer ${import.meta.env.VITE_PROXIMIIO_TOKEN}`,
              },
            },
          );

          if (imageResponse.status === 429) {
            setFetchStatus(FetchStatus.TooManyRequests);
            return null; // skip this image
          }

          if (!imageResponse.ok) {
            throw new Error(
              `Image fetch failed with status ${imageResponse.status}`,
            );
          }

          const imageResult = await imageResponse.json();
          const imageData = imageResult.data as ParkAssistImage;

          return {
            id: imageData.id,
            bayNo: imageData.bayNo,
            fullImageUrl: imageData.fullImageUrl,
          };
        });

        const imageData = await Promise.all(imagePromises);
        //@ts-ignore
        setImageData(imageData);
        setFetchStatus(FetchStatus.Loaded);
      } catch (error) {
        console.error("Error fetching image data:", error);
        // Set error state
        setFetchStatus(FetchStatus.Error);
      }
    };

    if (plateText) {
      fetchImageData();
    }
  }, [plateText, appSession]);

  return [imageData, fetchStatus];
}
